import React, { useEffect, useState } from "react";
import { columnSummary } from "./StatisticsTable";
import Chart from "react-apexcharts";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import SelectField from "../../components/common/SelectField.tsx";

interface SummaryModalProps {
    open: boolean;
    setOpen: any;
    selectedColumn: string | null;
    columnData: columnSummary;
    setSelectedColumn: any;
    setColumnData: any;
}

const SummaryModal: React.FC<SummaryModalProps> = ({ open, setOpen, selectedColumn, setSelectedColumn, columnData, setColumnData }) => {
    const [yearOptions, setYearOptions] = useState<string[]>([]);
    const [selectedYear, setSelectedYear] = useState<string | null>(null);
    const handleCloseModal = () => {
        setOpen(false)
        setSelectedColumn(null);
        setColumnData(null);
        setSelectedYear("All")
        setYearOptions([]);
    }

    useEffect(() => {
        if (columnData?.chart_data?.xaxis) {
            const years = new Set<string>();
            columnData.chart_data.xaxis.forEach((date) => {
                const year = date.split("-")[0];
                years.add(year);
            });
            setYearOptions(["All", ...Array.from(years)].map((year) => ({
                label: year,
                value: year,
            })));
            setSelectedYear({ label: "All", value: "All" });
        }
    }, [columnData]);


    const displayKey = (name: string) => {
        if (name === "mode")
            return "Most Frequent Value";
        else
            return name?.split("_").map(name => name[0].toUpperCase() + name.slice(1))?.join(" ");
    }

    const formatedDate = (fullDate: string) => {
        const date = new Date(fullDate);
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };
    const renderChart = () => {
        const { chart_type, chart_data } = columnData || {};
        const isBarChart = chart_type === "bar";
        const isDonutChart = chart_type === "donut";
        const isPieChart = chart_type === "pie";
        const isLineChart = chart_type === "line";

        const safeSeriesData = Array.isArray(chart_data?.series) ? chart_data.series : [0];
        const safeXaxis = Array.isArray(chart_data?.xaxis) ? chart_data.xaxis : [""];
        let filteredXAxis = safeXaxis;
        let filteredSeries = safeSeriesData;

        if (
            isLineChart &&
            selectedYear &&
            selectedYear.value !== "All"
        ) {
            const pairs = safeXaxis.map((date, index) => ({
                date,
                value: safeSeriesData[index],
            }));

            const filtered = pairs.filter((item) =>
                item.date.startsWith(selectedYear.value)
            );

            filteredXAxis = filtered.map((item) => item.date);
            filteredSeries = filtered.map((item) => item.value);
        }


        const chartOptions = {
            ...(isBarChart && {
                chart: {
                    type: "bar",
                    toolbar: {
                        show: true,
                        tools: {
                            download: true,
                            reset: true
                        }
                    }
                },
                colors: ["#7FC8D9", "#5A7FA5"],
                xaxis: { categories: safeXaxis },
                plotOptions: {
                    bar: {
                        distributed: true,
                        columnWidth: "40%"
                    }
                },
                legend: { show: false },
            }),
            ...(isLineChart && {
                chart: {
                    type: "line",
                    toolbar: {
                        show: true,
                        tools: {
                            download: true,
                            selection: false,
                            zoom: false,
                            zoomin: false,
                            zoomout: false,
                            pan: false,
                            reset: false
                        }
                    }
                },
                colors: ["#EF4444"],
                xaxis: { categories: filteredXAxis },
                stroke: {
                    width: 3,
                }
            }),
            ...(isDonutChart && {
                chart: {
                    type: "donut",
                    toolbar: {
                        show: true,
                        offsetY: -20,
                        tools: {
                            download: true,
                            reset: true
                        }
                    }
                },
                labels: chart_data?.labels || safeXaxis,
                stroke: {
                    show: true,
                    width: 3,
                    colors: ['#fff']
                },
                colors: ["#7FC8D9", "#5A7FA5"],
                legend: {
                    show: true,
                    position: "right",
                    horizontalAlign: "center",
                    fontSize: "14px",
                    itemMargin: {
                        vertical: 8,
                        horizontal: 15
                    },
                    formatter: function (seriesName, opts) {
                        const value = opts.w.globals.series[opts.seriesIndex];
                        const isNonNull = seriesName === 'Non-Null' || seriesName === 'Non-Unique';
                        const color = opts.w.globals.colors[opts.seriesIndex];
                        return `<span style="display: flex; align-items: center; padding: ${isNonNull ? '12px 16px' : '12px 16px'}; border: ${isNonNull ? '1px solid #d0e8f2' : '1px solid #bebdbdff'}; border-radius: 12px; background-color: ${isNonNull ? '#e8f4f8' : '#fbfbfbff'}; min-width: 180px;">
                                    <span style="width: 12px; height: 12px; border-radius: 50%; background-color: ${color}; display: inline-block; margin-right: 10px; flex-shrink: 0;"></span>
                                    <span style="display: flex; flex-direction: column;">
                                        <span style="font-weight: ${isNonNull ? '600' : '500'}; font-size: 14px; color: ${isNonNull ? '#333' : '#999'}; margin-bottom: 2px;">${seriesName}</span>
                                        <span style="color: ${isNonNull ? '#0066cc' : '#999'}; font-size: 12px; font-weight: 400;">${value.toLocaleString()} records</span>
                                    </span>
                                </span>`;
                    },
                    markers: {
                        width: 0,
                        height: 0,
                    },
                    onItemClick: {
                        toggleDataSeries: false
                    },
                    onItemHover: {
                        highlightDataSeries: true
                    }
                },
                dataLabels: {
                    enabled: false
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: false
                            }
                        },
                        expandOnClick: false
                    }
                },
                tooltip: {
                    enabled: true,
                    y: {
                        formatter: function (value) {
                            return value.toLocaleString() + " records";
                        }
                    }
                }
            }),
            ...(isPieChart && {
                chart: {
                    type: "pie",
                    toolbar: {
                        show: true,
                        offsetY: -20,
                        tools: {
                            download: true,
                            reset: true
                        }
                    }
                },
                labels: chart_data?.labels || safeXaxis,
                stroke: {
                    show: true,
                    width: 3,
                    colors: ['#fff']
                },
                colors: ["#7FC8D9", "#5A7FA5"],
                legend: {
                    show: true,
                    position: "right",
                    horizontalAlign: "center",
                    fontSize: "14px",
                    itemMargin: {
                        vertical: 8,
                        horizontal: 15
                    },
                    formatter: function (seriesName, opts) {
                        const value = opts.w.globals.series[opts.seriesIndex];
                        const isNonNull = seriesName === 'Non-Null';
                        const color = opts.w.globals.colors[opts.seriesIndex];
                        return `<span style="display: flex; align-items: center; padding: ${isNonNull ? '12px 16px' : '12px 16px'}; border: ${isNonNull ? '1px solid #d0e8f2' : '1px solid #bebdbdff'}; border-radius: 12px; background-color: ${isNonNull ? '#e8f4f8' : '#fbfbfbff'}; min-width: 180px;">
                                    <span style="width: 12px; height: 12px; border-radius: 50%; background-color: ${color}; display: inline-block; margin-right: 10px; flex-shrink: 0;"></span>
                                    <span style="display: flex; flex-direction: column;">
                                        <span style="font-weight: ${isNonNull ? '600' : '500'}; font-size: 14px; color: ${isNonNull ? '#333' : '#999'}; margin-bottom: 2px;">${seriesName}</span>
                                        <span style="color: ${isNonNull ? '#0066cc' : '#999'}; font-size: 12px; font-weight: 400;">${value.toLocaleString()} records</span>
                                    </span>
                                </span>`;
                    },
                    markers: {
                        width: 0,
                        height: 0,
                    },
                    onItemClick: {
                        toggleDataSeries: false
                    },
                    onItemHover: {
                        highlightDataSeries: true
                    }
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + "%";
                    },
                    style: {
                        fontSize: '16px',
                        fontWeight: 'bold',
                        colors: ['#fff']
                    },
                    dropShadow: {
                        enabled: false
                    }
                },
                plotOptions: {
                    pie: {
                        expandOnClick: false
                    }
                },
                tooltip: {
                    y: {
                        formatter: function (value) {
                            return value.toLocaleString() + " records";
                        }
                    }
                }
            }),
        };

        const series = isBarChart ? [{ name: "Data", data: safeSeriesData }] : isLineChart ? [{ name: "Data", data: filteredSeries }] : safeSeriesData;

        return (
            <>
                <div style={{ position: "relative" }} className="mt-4">
                    {isLineChart && (
                        <div
                            style={{
                                position: "absolute",
                                top: -3,
                                right: 35,
                                zIndex: 10,
                                width: 100,
                            }}
                        >
                            <SelectField
                                value={selectedYear}   // ✅ correct
                                placeholder="Year"
                                options={yearOptions}
                                isClearable={false}
                                handleChange={(selectedOption) => {
                                    setSelectedYear(selectedOption);
                                }}
                                isRVStyle={true}
                            />
                        </div>
                    )}


                    <Chart
                        height="300"
                        width="100%"
                        type={chart_type}
                        options={chartOptions}
                        series={series}
                    />
                </div>
            </>
        );
    };

    return (
        <Modal
            backdrop="static"
            centered={true}
            size={"lg"}
            toggle={handleCloseModal}
            isOpen={open && columnData}
            className="rv-modal"
        >
            <ModalHeader className="w-100 justify-content-center p-13 position-relative rv-breadcrumb-border ">
                {selectedColumn}
                <button
                    type="button"
                    className="close-button-currency position-absolute top-0 end-0 mt-1 me-3 border-0 bg-transparent fs-3"
                    onClick={handleCloseModal}
                >
                    &times;
                </button>
            </ModalHeader>
            <ModalBody className="fs-5 fw-bold d-block" style={{padding:"24px"}}>
                <div className="row g-3">
                    {columnData && Object.entries(columnData?.statistics_data)?.map(([key, val]) => {
                        if (val != null && key !== "column_name") {
                            return (
                                <div key={key} className="col-md-6 col-lg-4">
                                    <div className="d-flex flex-column" style={{ fontSize: '12px' }}>
                                        <span className="text-muted fw-normal mb-1">{displayKey(key)}</span>
                                        <span
                                            className="fw-semibold fs-6 text-dark"
                                            title={String(val)}
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {(columnData?.chart_type === "line" && (key === "earliest" || key === "latest")) ? formatedDate(val) : String(val)}
                                        </span>
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })}
                </div>
                <hr style={{ color: "#9b9c9dff" }} />
                {columnData?.chart_data?.series?.length > 0 && renderChart()}
            </ModalBody>
        </Modal>
    )
}
export default SummaryModal;